import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList, categoryLabels } from '@/mocks/manga';
import type { Manga } from '@/mocks/manga';

const categoryMeta: Record<string, { title: string; desc: string; keywords: string[] }> = {
  kr: {
    title: '韩漫',
    desc: '韩国漫画精选合集，汇集全网最热门的韩国漫画作品。剧情精彩，画风精美，免费全集在线阅读。',
    keywords: ['热血', '恋爱', '奇幻', '战斗', '校园'],
  },
  jp: {
    title: '日漫',
    desc: '经典日本漫画大全，从热血少年漫到搞笑日常番，应有尽有。免费在线阅读，高清画质体验。',
    keywords: ['冒险', '热血', '搞笑', '经典', '战斗'],
  },
  cn: {
    title: '国漫',
    desc: '国产漫画精选推荐，国漫崛起正当时。国风奇幻、都市异能、修真修仙，精彩不断。',
    keywords: ['奇幻', '热血', '修炼', '国风', '现代'],
  },
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const validType = (type && ['kr', 'jp', 'cn'].includes(type) ? type : 'kr') as Manga['category'];
  const [currentTab, setCurrentTab] = useState<Manga['category']>(validType);
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  useEffect(() => {
    if (type && ['kr', 'jp', 'cn'].includes(type)) {
      setCurrentTab(type as Manga['category']);
    }
  }, [type]);

  useEffect(() => {
    const meta = categoryMeta[currentTab];
    document.title = `星星动漫网${meta.title} - ${meta.title}漫画大全 | 好看的${meta.title}免费在线阅读`;
  }, [currentTab]);

  const filtered = mangaList
    .filter((m) => m.category === currentTab)
    .filter((m) => statusFilter === 'all' || m.status === statusFilter)
    .sort((a, b) => {
      if (sortBy === 'popular') return b.views - a.views;
      if (sortBy === 'rating') return b.rating - a.rating;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  const meta = categoryMeta[currentTab];

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: `${meta.title}漫画` },
  ];

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `星星动漫网${meta.title}漫画大全`,
    url: `https://www.xingxingdongman.com/category/${currentTab}`,
    description: meta.desc,
    isPartOf: {
      '@type': 'WebSite',
      name: '星星动漫网',
      url: 'https://www.xingxingdongman.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />

      <Navbar />

      <main className="min-h-screen bg-background-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-8" aria-labelledby="category-heading">
            <h1 id="category-heading" className="font-heading text-3xl font-bold text-foreground-900 mb-3">
              {meta.title}漫画大全
            </h1>
            <p className="text-foreground-600 text-sm max-w-2xl">{meta.desc}</p>
          </section>

          <div className="flex flex-wrap items-center gap-2 mb-6 bg-background-100/80 rounded-full px-1 py-1 w-fit" role="tablist" aria-label="漫画分类切换">
            {(Object.entries(categoryLabels) as [Manga['category'], string][]).map(([key, label]) => (
              <Link
                key={key}
                to={`/category/${key}`}
                role="tab"
                aria-selected={currentTab === key}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                  currentTab === key
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center bg-background-100 rounded-full px-1 py-1" role="tablist" aria-label="状态筛选">
                {(['all', '连载中', '已完结'] as const).map((s) => (
                  <button
                    key={s}
                    role="tab"
                    aria-selected={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                      statusFilter === s
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    {s === 'all' ? '全部' : s}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {(['popular', 'latest', 'rating'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSortBy(s)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                    sortBy === s
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {s === 'popular' ? '最热门' : s === 'latest' ? '最新' : '最高评分'}
                </button>
              ))}
            </div>
          </div>

          <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-16" aria-label={`${meta.title}漫画列表`}>
            {filtered.map((manga) => (
              <Link
                key={manga.id}
                to={`/article/${manga.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - 星星动漫网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                      manga.status === '连载中' ? 'bg-primary-500/90' : 'bg-accent-500/90'
                    }`}>
                      {manga.status}
                    </span>
                  </div>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {manga.title}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-foreground-500">{manga.author}</span>
                  <span className="text-xs text-yellow-500 flex items-center gap-0.5">
                    <i className="ri-star-fill text-[10px]"></i>
                    {manga.rating}
                  </span>
                </div>
                <p className="text-xs text-foreground-400 mt-0.5">
                  {manga.chapters.length}话 · {manga.views >= 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views}次阅读
                </p>
              </Link>
            ))}
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}